import { Box, Button, Typography } from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { flexMixin } from 'utils/flexMixin';

import { NamespacesList } from 'core/enums/common';

import { IFormSummaryStory } from 'components/TicketSteps/GeneralStep/EditGeneralStep';
import { ExpandableTextField } from 'components/shared/ExpandableTextField';

import { ReactComponent as Refresh } from 'assets/icons/refresh.svg';
import { titleTextColor } from 'assets/styles/_palette';

import * as Styled from './styled';

interface IDetailedStoryViewProps {
	story: IFormSummaryStory | undefined;
	storyIndex: number;
	register: any;
	errors: any;
	handleDeleteScenario: (storyIndex: number, scenarioIndex: number) => void;
	handleRefreshScenario: (storyIndex: number, scenarioIndex: number) => void;
	handleDeleteAcceptanceCriteria: (storyIndex: number, criteriaIndex: number) => void;
	handleRefreshAcceptanceCriteria: (storyIndex: number, criteriaIndex: number) => void;
}

export const EditDetailedStoryView: FC<IDetailedStoryViewProps> = ({
	story,
	storyIndex,
	register,
	errors,
	handleDeleteScenario,
	handleRefreshScenario,
	handleDeleteAcceptanceCriteria,
	handleRefreshAcceptanceCriteria,
}) => {
	const { t: generalStepTranslate } = useTranslation(NamespacesList['generalStep']);

	const [activeTab, setActiveTab] = useState(generalStepTranslate('commonText.scenarios'));

	const tabs: Array<string> = useMemo(() => {
		const tabsArray = [];

		if (story?.summaryScenarios?.length)
			tabsArray.push(generalStepTranslate('commonText.scenarios'));

		if (story?.summaryAcceptanceCriteria?.length)
			tabsArray.push(generalStepTranslate('commonText.acceptanceCriteria'));

		return tabsArray;
	}, [story?.summaryScenarios, story?.summaryAcceptanceCriteria]);

	return (
		<>
			{story ? (
				<Box
					sx={{
						width: '50%',
						boxShadow: '0px 4px 16px 0px #2867830F',
						position: 'sticky',
						height: 'fit-content',
						top: 0,
					}}
				>
					<Box
						sx={{
							boxSizing: 'border-box',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							width: '100%',
							padding: '12px',
							marginBottom: '12px',
							borderBottom: '1px solid #E7E8EC',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								gap: '12px',
							}}
						>
							{tabs?.map((tab, index) => (
								<Styled.DetailedStoryViewTab
									key={index}
									active={tab === activeTab}
									onClick={() => setActiveTab(tab)}
								>
									{tab}
								</Styled.DetailedStoryViewTab>
							))}
						</Box>
						<Box
							sx={{
								display: 'flex',
								gap: '24px',
							}}
						></Box>
					</Box>
					{story?.summaryScenarios?.length &&
					activeTab === generalStepTranslate('commonText.scenarios') ? (
						<>
							<Typography
								sx={{
									fontWeight: 700,
									marginBottom: '8px',
									paddingLeft: '16px',
									color: titleTextColor,
								}}
							>
								{generalStepTranslate('commonText.scenarios')}
							</Typography>

							{story?.summaryScenarios?.length
								? story.summaryScenarios.map((scenario, scenarioIndex) => (
										<Box
											key={scenario.id}
											sx={{
												...flexMixin({
													justify: 'flex-start',
													align: 'flex-start',
													direction: 'column',
												}),
												width: '100%',
												padding: '0 0 0 16px',
												boxSizing: 'border-box',
											}}
										>
											<Box
												sx={{
													...flexMixin({
														justify: 'flex-end',
													}),
													width: '100%',
												}}
											>
												{!scenario.isDeleted ? (
													<Button
														type="button"
														sx={{
															minWidth: '24px',
															height: '24px',
															fontSize: '24px',
															color: '#9FA6B3',
														}}
														onClick={() => handleDeleteScenario(storyIndex, scenarioIndex)}
													>
														<span>&times;</span>
													</Button>
												) : (
													<Button
														type="button"
														sx={{
															minWidth: '24px',
															height: '24px',
															fontSize: '24px',
															color: '#9FA6B3',
														}}
														onClick={() => handleRefreshScenario(storyIndex, scenarioIndex)}
													>
														<Refresh />
													</Button>
												)}
											</Box>
											<ExpandableTextField
												errors={
													errors?.['ticketSummaries']?.[storyIndex]?.['summaryScenarios']?.[
														scenarioIndex
													]?.['title']
												}
												registerConfig={register(
													`ticketSummaries.${storyIndex}.summaryScenarios.${scenarioIndex}.title`
												)}
												isDisabled={scenario.isDeleted}
												defaultValue={scenario.title}
											/>

											<ExpandableTextField
												errors={
													errors?.['ticketSummaries']?.[storyIndex]?.['summaryScenarios']?.[
														scenarioIndex
													]?.['description']
												}
												registerConfig={register(
													`ticketSummaries.${storyIndex}.summaryScenarios.${scenarioIndex}.description`
												)}
												isDisabled={scenario.isDeleted}
												defaultValue={scenario.description}
											/>
										</Box>
								  ))
								: null}
						</>
					) : null}
					{story?.summaryAcceptanceCriteria?.length &&
					activeTab === generalStepTranslate('commonText.acceptanceCriteria') ? (
						<>
							<Typography
								sx={{
									fontWeight: 700,
									marginBottom: '8px',
									paddingLeft: '16px',
									color: titleTextColor,
								}}
							>
								{generalStepTranslate('commonText.acceptanceCriteria')}
							</Typography>
							{story?.summaryAcceptanceCriteria?.length ? (
								<>
									{story.summaryAcceptanceCriteria.map((criteria, criteriaIndex) => (
										<Box
											key={criteria.id}
											sx={{
												...flexMixin({
													justify: 'flex-start',
													align: 'flex-start',
													direction: 'column',
												}),
												width: '100%',
												padding: '0 0 0 16px',
												boxSizing: 'border-box',
											}}
										>
											<Box
												sx={{
													...flexMixin({
														justify: 'flex-end',
													}),
													width: '100%',
												}}
											>
												{!criteria.isDeleted ? (
													<Button
														type="button"
														sx={{
															minWidth: '24px',
															height: '24px',
															fontSize: '24px',
															color: '#9FA6B3',
														}}
														onClick={() =>
															handleDeleteAcceptanceCriteria(storyIndex, criteriaIndex)
														}
													>
														<span>&times;</span>
													</Button>
												) : (
													<Button
														type="button"
														sx={{
															minWidth: '24px',
															height: '24px',
															fontSize: '24px',
															color: '#9FA6B3',
														}}
														onClick={() =>
															handleRefreshAcceptanceCriteria(storyIndex, criteriaIndex)
														}
													>
														<Refresh />
													</Button>
												)}
											</Box>

											<ExpandableTextField
												errors={
													errors?.['ticketSummaries']?.[storyIndex]?.[
														'summaryAcceptanceCriteria'
													]?.[criteriaIndex]?.['title']
												}
												registerConfig={register(
													`ticketSummaries.${storyIndex}.summaryAcceptanceCriteria.${criteriaIndex}.title`
												)}
												isDisabled={criteria.isDeleted}
												defaultValue={criteria.title}
											/>

											<ExpandableTextField
												errors={
													errors?.['ticketSummaries']?.[storyIndex]?.[
														'summaryAcceptanceCriteria'
													]?.[criteriaIndex]?.['description']
												}
												registerConfig={register(
													`ticketSummaries.${storyIndex}.summaryAcceptanceCriteria.${criteriaIndex}.description`
												)}
												isDisabled={criteria.isDeleted}
												defaultValue={criteria.description}
											/>
										</Box>
									))}
								</>
							) : null}
						</>
					) : null}
				</Box>
			) : null}
		</>
	);
};
